import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/ui/empty";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    activateAdmin,
    createAdmin,
    deactivateAdmin,
    deleteAdmin,
    listAdminsPaged,
    updateAdmin,
    type CreateAdminRequest,
    type UpdateAdminRequest,
} from "@/api/admins";
import { Pagination } from "@/components/pagination";
import { apiErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { AdminDto } from "@/types/api";
import { useAuth } from "@/hooks/use-auth";

export function AdminsPage(): React.ReactNode {
    const queryClient = useQueryClient();
    const { admin: currentAdmin } = useAuth();
    const confirm = useConfirm();
    const [creating, setCreating] = React.useState(false);
    const [editing, setEditing] = React.useState<AdminDto | null>(null);

    const PAGE_SIZE = 20;
    const [pageIndex, setPageIndex] = React.useState(0);
    const listQuery = useQuery({
        queryKey: ["admins", pageIndex],
        queryFn: () => listAdminsPaged({ page: pageIndex, size: PAGE_SIZE, sort: "id,asc" }),
        placeholderData: (prev) => prev,
    });

    const createMut = useMutation({
        mutationFn: (req: CreateAdminRequest) => createAdmin(req),
        onSuccess: () => {
            toast.success("Admin created");
            queryClient.invalidateQueries({ queryKey: ["admins"] });
            setCreating(false);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Create failed")),
    });

    const updateMut = useMutation({
        mutationFn: ({ id, request }: { id: number; request: UpdateAdminRequest }) =>
            updateAdmin(id, request),
        onSuccess: () => {
            toast.success("Admin updated");
            queryClient.invalidateQueries({ queryKey: ["admins"] });
            setEditing(null);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Update failed")),
    });

    const deleteMut = useMutation({
        mutationFn: (id: number) => deleteAdmin(id),
        onSuccess: () => {
            toast.success("Admin deleted");
            queryClient.invalidateQueries({ queryKey: ["admins"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    const setActiveMut = useMutation({
        mutationFn: ({ id, active }: { id: number; active: boolean }) =>
            active ? activateAdmin(id) : deactivateAdmin(id),
        onSuccess: (dto) => {
            toast.success(dto.active ? "Admin reinstated" : "Admin suspended");
            queryClient.invalidateQueries({ queryKey: ["admins"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Update failed")),
    });

    // Backend refuses suspend for bootstrap (403) and self (409) — pre-disable
    // the toggle with an explanation instead of letting the call bounce.
    const suspendDisabledReason = (a: AdminDto): string | null => {
        if (a.isBootstrap) return "The owner (bootstrap) admin can't be suspended";
        if (a.id === currentAdmin?.id) return "You can't suspend your own account";
        return null;
    };

    const toggleActive = (a: AdminDto, next: boolean): void => {
        if (next) {
            setActiveMut.mutate({ id: a.id, active: true });
            return;
        }
        confirm.show({
            title: `Suspend ${a.email}?`,
            description: "Suspended admins can't log in and are signed out immediately.",
            destructive: true,
            confirmLabel: "Suspend",
            onConfirm: async () => {
                await setActiveMut.mutateAsync({ id: a.id, active: false });
            },
        });
    };

    return (
        <>
            <PageHeader
                title="Admins"
                description="Team members with access to this admin panel. The first admin (bootstrap) can't be removed."
                actions={
                    <Button data-testid="admin-new" onClick={() => setCreating(true)}>
                        <Plus className="h-4 w-4" />
                        New admin
                    </Button>
                }
            />
            <div className="p-6">
                <Card>
                    <CardContent className="p-0">
                        {listQuery.isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Spinner size="lg" />
                            </div>
                        ) : listQuery.data && listQuery.data.content.length > 0 ? (
                            <DataTable<AdminDto>
                                dataTestid="admins-table"
                                className="rounded-none border-0"
                                columns={[
                                    {
                                        key: "email",
                                        header: "Email",
                                        cell: (a) => (
                                            <span className="font-medium">
                                                {a.email}
                                                {currentAdmin?.id === a.id && (
                                                    <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                                                )}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: "bootstrap",
                                        header: "Role",
                                        cell: (a) =>
                                            a.isBootstrap ? (
                                                <Badge variant="success" className="gap-1" title="The first admin created — can't be deleted, immune to teardown scripts.">
                                                    <ShieldCheck className="h-3 w-3" />
                                                    Owner
                                                </Badge>
                                            ) : (
                                                <Badge variant="muted">Admin</Badge>
                                            ),
                                    },
                                    {
                                        key: "status",
                                        header: "Status",
                                        cell: (a) =>
                                            a.active ? (
                                                <Badge variant="success" data-testid={`admin-status-${a.id}`}>Active</Badge>
                                            ) : (
                                                <Badge
                                                    variant="destructive"
                                                    data-testid={`admin-status-${a.id}`}
                                                    title="Login blocked; existing sessions revoked."
                                                >
                                                    Suspended
                                                </Badge>
                                            ),
                                    },
                                    {
                                        key: "created",
                                        header: "Created",
                                        cell: (a) => (
                                            <span className="text-xs text-muted-foreground">
                                                {formatDate(a.createdAt)}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: "actions",
                                        header: "",
                                        cell: (a) => (
                                            <div className="flex items-center justify-end gap-1">
                                                {/* Wrapping span carries the tooltip — disabled buttons
                                                    don't reliably surface `title` on their own. */}
                                                <span
                                                    className="mr-1"
                                                    title={
                                                        suspendDisabledReason(a) ??
                                                        (a.active
                                                            ? "Suspend — blocks login and signs them out immediately"
                                                            : "Reinstate — lets this admin log in again")
                                                    }
                                                >
                                                    <Switch
                                                        checked={a.active}
                                                        disabled={suspendDisabledReason(a) != null || setActiveMut.isPending}
                                                        onCheckedChange={(next) => toggleActive(a, next)}
                                                        data-testid={`admin-active-toggle-${a.id}`}
                                                    />
                                                </span>
                                                <Button
                                                    data-testid={`admin-edit-${a.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditing(a);
                                                    }}
                                                    title="Edit email or reset password"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    data-testid={`admin-delete-${a.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    disabled={a.isBootstrap || a.id === currentAdmin?.id}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        confirm.show({
                                                            title: `Delete admin "${a.email}"?`,
                                                            description: "This action cannot be undone.",
                                                            destructive: true,
                                                            confirmLabel: "Delete",
                                                            onConfirm: async () => {
                                                                await deleteMut.mutateAsync(a.id);
                                                            },
                                                        });
                                                    }}
                                                    title={
                                                        a.isBootstrap
                                                            ? "Owner admin can't be deleted"
                                                            : a.id === currentAdmin?.id
                                                                ? "You can't delete yourself"
                                                                : "Delete admin"
                                                    }
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        ),
                                    },
                                ]}
                                rows={listQuery.data.content}
                                rowKey={(a) => a.id}
                            />
                        ) : (
                            <EmptyState title="No admins" description="Create the first admin to get started." />
                        )}
                    </CardContent>
                </Card>
                {listQuery.data && (
                    <Pagination
                        page={listQuery.data}
                        pageSize={PAGE_SIZE}
                        currentPage={pageIndex}
                        onPage={setPageIndex}
                    />
                )}
            </div>

            <AdminForm
                open={creating}
                mode="create"
                initial={{ email: "", password: "" }}
                onClose={() => setCreating(false)}
                submitting={createMut.isPending}
                onSubmit={(form) => createMut.mutate(form)}
            />
            <AdminForm
                open={editing != null}
                mode="edit"
                initial={{ email: editing?.email ?? "", password: "" }}
                onClose={() => setEditing(null)}
                submitting={updateMut.isPending}
                onSubmit={(form) => {
                    if (!editing) return;
                    const patch: UpdateAdminRequest = {};
                    if (form.email && form.email !== editing.email) patch.email = form.email;
                    if (form.password) patch.password = form.password;
                    if (!patch.email && !patch.password) {
                        toast.info("Nothing changed");
                        setEditing(null);
                        return;
                    }
                    updateMut.mutate({ id: editing.id, request: patch });
                }}
            />
            {confirm.dialog}
        </>
    );
}

interface FormState {
    email: string;
    password: string;
}

interface AdminFormProps {
    open: boolean;
    mode: "create" | "edit";
    initial: FormState;
    onClose: () => void;
    submitting: boolean;
    onSubmit: (form: FormState) => void;
}

function AdminForm({ open, mode, initial, onClose, submitting, onSubmit }: AdminFormProps): React.ReactNode {
    const [form, setForm] = React.useState<FormState>(initial);
    // Reset only when the dialog OPENS — not on every parent re-render. `initial`
    // is a fresh object each render, so keying the reset on it would wipe the
    // operator's in-progress edits on any background/refocus refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (open) setForm(initial);
    }, [open]);

    const canSubmit =
        mode === "create"
            ? /^\S+@\S+\.\S+$/.test(form.email) && form.password.length >= 8
            : (!form.email || /^\S+@\S+\.\S+$/.test(form.email)) && (!form.password || form.password.length >= 8);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "New admin" : "Edit admin"}</DialogTitle>
                    <DialogDescription>
                        {mode === "create"
                            ? "New team member will be able to log in immediately."
                            : "Leave password blank to keep it unchanged."}
                    </DialogDescription>
                </DialogHeader>
                <form
                    data-testid="admin-form"
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSubmit(form);
                    }}
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="admin-email">Email</Label>
                        <Input
                            id="admin-email"
                            data-testid="admin-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="team@landingmanager.uk"
                            autoComplete="off"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="admin-password">
                            <KeyRound className="mr-1.5 inline h-3 w-3" />
                            Password {mode === "edit" && <span className="text-muted-foreground">(optional)</span>}
                        </Label>
                        <Input
                            id="admin-password"
                            data-testid="admin-password"
                            type="password"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            placeholder={mode === "edit" ? "(unchanged)" : "At least 8 characters"}
                            autoComplete="new-password"
                        />
                        {form.password.length > 0 && <PasswordStrength password={form.password} />}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!canSubmit || submitting}
                            data-testid="admin-submit"
                        >
                            {submitting ? "Saving…" : mode === "create" ? "Create" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Crude visual password-strength meter. Scoring:
 *   length 8+ → 1, 12+ → 2, 16+ → 3
 *   has lower / upper / digit / symbol → +1 each
 *   common-pattern penalties (all letters, all digits, repeats) → −1
 * Capped at 5. Renders a 5-segment bar + a one-word label.
 *
 * Note: the 8-char server minimum is enforced separately — this is a
 * nudge, not a gate. We don't block submit on weak passwords because
 * "Owner says use 'password1'" is a real (terrible) user need.
 */
function PasswordStrength({ password }: { password: string }): React.ReactNode {
    const score = scorePassword(password);
    const labels = ["Very weak", "Weak", "Fair", "Strong", "Excellent"];
    const colors = [
        "bg-destructive/70",
        "bg-amber-500/70",
        "bg-amber-400/70",
        "bg-emerald-500/70",
        "bg-emerald-600",
    ];
    return (
        <div className="space-y-1">
            <div className="flex gap-1">
                {Array.from({ length: 5 }, (_, i) => (
                    <div
                        key={i}
                        className={`h-1 flex-1 rounded-full ${i < score ? colors[Math.max(0, score - 1)] : "bg-muted"}`}
                    />
                ))}
            </div>
            <div className="text-[10px] text-muted-foreground">
                {labels[Math.max(0, score - 1)] ?? "Very weak"} · {password.length < 12 ? "use 12+ chars for a real bump" : "long enough"}
            </div>
        </div>
    );
}

function scorePassword(p: string): number {
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (p.length >= 16) s++;
    if (/[a-z]/.test(p)) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    if (/^[a-zA-Z]+$/.test(p) || /^[0-9]+$/.test(p)) s -= 2;
    if (/(.)\1{2,}/.test(p)) s--; // 3+ repeats
    return Math.max(0, Math.min(5, s - 2)); // shift so "8 chars + lowercase only" reads as 1
}
